/* @jsx h */
import { defineComponent, computed, h, type Ref } from "vue";
import {
  type FloorPlan,
  type TableStatus,
  type Reservation,
} from "@lemmonhq/types";
import { TablesFloorPlanView } from "../floor-plan";
import { TablesGrid } from "../TablesGrid";
import { useAuth } from "../../../providers";
import { type FloorPlanViewMode, type DropModeState } from "../tables-types";

export const TablesBody = defineComponent<{
  currentView: Ref<string>;
  fetchTables: Function;
  filteredTables: Ref<TableStatus[]>;
  tables: Ref<TableStatus[]>;
  floorPlans: Ref<FloorPlan[]>;
  activeFloorPlan: Ref<string | undefined>;
  currentFloorPlan: Ref<FloorPlan | null>;
  onFloorPlanChange: (fp: FloorPlan) => void;
  isLoading: Ref<boolean>;
  onShowQR: (tableId: number) => void;
  floorPlanViewMode: Ref<FloorPlanViewMode>;
  dropModeState: Ref<DropModeState>;
  onTableClickInDropMode: (tableId: number) => void;
  reservations: Ref<Reservation[]>;
  isLiveDay: Ref<boolean>;
  nowMs: number;
}>({
  name: "TablesBody",
  props: [
    "currentView",
    "fetchTables",
    "filteredTables",
    "floorPlans",
    "tables",
    "activeFloorPlan",
    "currentFloorPlan",
    "onFloorPlanChange",
    "isLoading",
    "onShowQR",
    "floorPlanViewMode",
    "dropModeState",
    "onTableClickInDropMode",
    "reservations",
    "isLiveDay",
    "nowMs",
  ],
  setup(props) {
    const auth = useAuth();
    const posMode = auth.posMode;
    const isEditable = computed(
      () => auth.user.value?.role === "manager" && !posMode.value,
    );
    return () =>
      props.currentView.value === "floor_plan" ? (
        <div class="flex-1 overflow-hidden relative">
          {props.floorPlanViewMode.value === "all" ? (
            <div
              class="overflow-auto h-full"
              style="touch-action: pan-x pan-y pinch-zoom;"
            >
              <div class="space-y-0">
                {props.floorPlans.value.map((floorPlan, idx) => (
                  <div
                    key={floorPlan.id}
                    id={`floor-plan-${floorPlan.id}`}
                    class="floor-plan-enter"
                    style={{
                      animationDelay: `${idx * 80}ms`,
                      background:
                        floorPlan.color || "var(--floor-plan-background)",
                    }}
                  >
                    <div class="flex items-center gap-2 px-3 py-2">
                      <span class="text-sm font-bold text-accent/70 uppercase tracking-wider">
                        {floorPlan.name}
                      </span>
                      <div class="flex-1 h-px bg-[var(--floor-plan-section-divider)]" />
                    </div>
                    <div
                      class="relative"
                      style={{ height: "calc(100vh - 210px)" }}
                    >
                      <TablesFloorPlanView
                        activeFloorPlan={{ value: floorPlan.id } as any}
                        tables={props.tables}
                        floorPlans={props.floorPlans}
                        currentFloorPlan={{ value: floorPlan } as any}
                        onFloorPlanChange={props.onFloorPlanChange}
                        onTablePayment={() => {
                          props.fetchTables();
                        }}
                        isEditable={false}
                        isLoading={props.isLoading}
                        dropModeState={props.dropModeState}
                        onTableClickInDropMode={props.onTableClickInDropMode}
                        reservations={props.reservations}
                        isLiveDay={props.isLiveDay.value}
                        nowMs={props.nowMs}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <TablesFloorPlanView
              activeFloorPlan={props.activeFloorPlan}
              tables={props.tables}
              floorPlans={props.floorPlans}
              currentFloorPlan={props.currentFloorPlan}
              onFloorPlanChange={props.onFloorPlanChange}
              onTablePayment={() => {
                props.fetchTables();
              }}
              isEditable={isEditable.value}
              isLoading={props.isLoading}
              dropModeState={props.dropModeState}
              onTableClickInDropMode={props.onTableClickInDropMode}
              reservations={props.reservations}
              isLiveDay={props.isLiveDay.value}
              nowMs={props.nowMs}
            />
          )}
        </div>
      ) : (
        <div class="flex-1 overflow-auto">
          <TablesGrid
            tables={props.filteredTables}
            onTablePayment={() => {
              props.fetchTables();
            }}
            onShowQR={props.onShowQR}
          />
        </div>
      );
  },
});
